import DownloadIcon from "@mui/icons-material/GetApp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CircularProgress } from "@mui/material";
import { saveAs } from "file-saver";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useImplementationGuide } from "../../api/useImplementationGuide";
import PageHeader from "../../components/Headers/PageHeader";
import ScrollPdfPreview from "../../components/PdfPreview/ScrollPdfPreview";

const ImplementationGuide: React.FC = () => {
  const [t] = useTranslation();
  const { client } = useParams() as { client: string };
  // Signed URLs expire, so every visit refetches instead of reusing the session-cached one.
  const { data: url, isFetching } = useImplementationGuide(client, { refetchOnMount: "always" });

  // The `download` attribute is ignored on cross-origin signed URLs, so save the fetched blob instead.
  const handleDownload = async (guideUrl: string) => {
    try {
      const response = await fetch(guideUrl);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      saveAs(await response.blob(), `${client}-implementation-guide.pdf`);
    } catch {
      window.open(guideUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div>
      <PageHeader title={t("implementationGuide.header")} />
      <div className="pt-6">
        {isFetching && (
          <div className="flex justify-center pt-20">
            <CircularProgress size={80} />
          </div>
        )}
        {!isFetching && !url && <p>{t("implementationGuide.unavailable")}</p>}
        {!isFetching && url && (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-4">
              <a
                className="inline-flex items-center underline text-brand"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("implementationGuide.openInNewTab")}
                <OpenInNewIcon className="ml-1" fontSize="small" />
              </a>
              <button
                type="button"
                className="inline-flex items-center underline text-brand cursor-pointer"
                onClick={() => handleDownload(url)}
              >
                {t("implementationGuide.download")}
                <DownloadIcon className="ml-1" fontSize="small" />
              </button>
            </div>
            <ScrollPdfPreview url={url} isLoading={false} client={client} presentation="continuous" />
          </>
        )}
      </div>
    </div>
  );
};

export default ImplementationGuide;
